const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getTimeDifferenceInMonths = (date1: Date, date2: Date): number => {
  if (
    date2?.getFullYear() === date1?.getFullYear() &&
    date2?.getMonth() === date1?.getMonth()
  ) {
    const maximumDaysInTheMonth = new Date(
      date1.getFullYear(),
      date1.getMonth(),
      0,
    ).getDate();
    return (date2?.getDate() - date1?.getDate()) / maximumDaysInTheMonth;
  }
  return (
    date2?.getMonth() -
    date1?.getMonth() +
    12 * (date2?.getFullYear() - date1?.getFullYear())
  );
};

export const getTextWidth = (
  text: string,
  fontFamily: string,
  fontSize: string,
): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    // Canvas not supported, return a default value
    return 0;
  }

  context.font = `${fontSize} ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
};

export const getMidTimeBetweenTwoDate = (date1: Date, date2: Date): Date => {
  return new Date((date1?.getTime() + date2?.getTime()) / 2);
};

export const decodeHtml = (html: any) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

const getPlural = (number: number, word: { one: string; other: string }) => {
  return (number === 1 && word.one) || word.other;
};

export const getWords = (monthCount: number) => {
  const months = { one: "m", other: "m" },
    years = { one: "yr", other: "yrs" },
    m = monthCount % 12,
    y = Math.floor(monthCount / 12),
    result = [];
  if (monthCount === 0) {
    return "1m";
  }
  y && result.push(y + " " + getPlural(y, years));
  m && result.push(m + " " + getPlural(m, months));
  return result.join("  ");
};

export const getdateMonth = (inputDate: string): string => {
  let date;
  if (!inputDate || isNaN(Date.parse(inputDate))) {
    date = new Date();
  } else {
    date = new Date(inputDate);
  }
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  return `${day} ${month}`;
};

export const getMonthYear = (dateString: Date | string) => {
  // input: 2022-11-30
  // output: Nov 2022

  const date = new Date(dateString);
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${month} ${year}`;
};

export const calculateDuration = (
  startDate: Date | string,
  endDate: Date | string,
) => {
  // input: 2020-11-30, 2023-08-01
  // output: 2 yrs, 8 m
  const start = new Date(startDate);
  const end = new Date(endDate);

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return `${years > 0 ? `${years} yr${years > 1 ? "s" : ""}, ` : ""}${months} m`;
};

export const calculateDaysUntil = (targetDateStr: Date) => {
  // to get days until a target date
  const currentDate = new Date();
  const targetDate = new Date(targetDateStr);
  const timeDifference = targetDate.getTime() - currentDate.getTime();

  const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
};

export const extractValues = (items: any[], keys: string[]) =>
  items.map((item) => {
    const extracted: any = {};
    keys.forEach((key) => {
      extracted[key] = item[key];
    });
    return extracted;
  });

export const getTextFromArray = (arr: { text: string }[]) => {
  return arr.map((val) => val.text).join(", ") ?? "";
};

export const yearData = Array.from({ length: 51 }, (_, i) => ({
  id: i,
  text: `${i} ${i === 1 || i === 0 ? "year" : "years"}`,
  value: i,
}));

export const monthData = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  text: `${i} ${i === 1 || i === 0 ? "month" : "months"}`,
  value: i,
}));

export const formatDate = (timestamp: number | null): string => {
  const date = timestamp ? new Date(timestamp) : new Date();
  const options = {
    day: "numeric" as "numeric",
    month: "short" as "short",
    year: "numeric" as "numeric",
  };
  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);
  const parts = formattedDate.split(" ");
  return `${parts[0]} ${parts[1]}, ${parts[2]}`;
};

export const getInitials = (name: string) => {
  if (!name) return "";
  const words = name.trim().split(" ");
  const initials =
    words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "");
  return initials.toUpperCase();
};

export const formatCurrency = (value: number, currency: string) => {
  if (currency === "INR") {
    if (value >= 10000000) {
      return Math.floor((value / 10000000) * 100) / 100 + "Cr";
    } else if (value >= 100000) {
      return Math.floor((value / 100000) * 100) / 100 + "L";
    }
  } else {
    if (value >= 1000000) {
      return Math.floor((value / 1000000) * 100) / 100 + "M";
    } else if (value >= 1000) {
      return Math.floor((value / 1000) * 100) / 100 + "k";
    }
  }
  return value.toString();
};

export const getParamFromUrl = (param: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

export const mobileMessageHandler = (type: string) => {
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
  if (isMobile && typeof window.MessageHandler?.postMessage === "function") {
    window.MessageHandler.postMessage(
      JSON.stringify({
        type,
      }),
    );
  }
};

export const handleFocusOnField = (fieldId: string, parentID = "") => {
  let field: HTMLElement | null = null;
  if (!fieldId) return;
  if (parentID) {
    field = document
      .getElementById(parentID)
      ?.querySelector(`#${fieldId}`) as HTMLElement | null;
  } else {
    field = document.getElementById(fieldId);
  }

  if (field) {
    field.focus();
    field.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};
